/**
 * Viewer hook — resolves the current Viewer from /auth/me. Used by
 * the App shell to gate routes + navigation, and by individual
 * screens that need to branch on internal vs client.
 *
 * Long stale time — viewer rarely changes within a session, and we
 * don't want to thrash this query on every screen visit.
 */

import { useQuery } from '@tanstack/react-query';

import { authApi } from '@beacon/api-client';
import type { Viewer } from '@beacon/core';

import { beaconClient } from '@/lib/api/client.js';

export const viewerKeys = {
  all: ['viewer'] as const,
  me: () => [...viewerKeys.all, 'me'] as const,
};

export function useViewer() {
  return useQuery({
    queryKey: viewerKeys.me(),
    queryFn: async (): Promise<Viewer | null> => {
      try {
        const res = await authApi.me(beaconClient);
        return res.viewer;
      } catch {
        return null;
      }
    },
    staleTime: 30 * 60_000,
    refetchOnWindowFocus: false,
  });
}

export function useIsInternal(): boolean {
  const { data } = useViewer();
  return data?.organizationType === 'internal';
}

export function useIsClient(): boolean {
  const { data } = useViewer();
  return data?.organizationType === 'client';
}
